import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';
import { generateKindnessId } from '../utils/kindnessId';

const { FiX, FiRefreshCw, FiUser, FiHeart } = FiIcons;

const UserIdModal = ({ onClose, onSetId, currentId }) => {
  const [inputId, setInputId] = useState('');
  const [generatedId, setGeneratedId] = useState(currentId || generateKindnessId());
  const [mode, setMode] = useState(currentId ? 'current' : 'generate');

  const handleSubmit = (e) => {
    e.preventDefault();
    if (mode === 'generate') {
      onSetId(generatedId);
    } else if (mode === 'enter' && inputId.trim()) {
      onSetId(inputId.trim());
    } else if (mode === 'current') {
      onClose();
    }
  };

  const handleGenerateNew = () => {
    setGeneratedId(generateKindnessId());
  };

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 modal-backdrop flex items-center justify-center z-50 p-4"
        onClick={onClose}
      >
        <motion.div
          initial={{ scale: 0.9, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          exit={{ scale: 0.9, opacity: 0 }}
          className="modal-content w-full max-w-md p-6"
          onClick={(e) => e.stopPropagation()}
        >
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center space-x-3">
              <div className="clay-button p-3">
                <SafeIcon icon={FiHeart} className="text-xl heart-logo" />
              </div>
              <h2 className="text-xl font-bold text-heading">Your Kindness ID</h2>
            </div>
            <button
              onClick={onClose}
              className="clay-button p-2 hover:transform hover:scale-105 transition-all duration-300 focus-visible"
              aria-label="Close dialog"
            >
              <SafeIcon icon={FiX} className="text-lg text-text" />
            </button>
          </div>

          <div className="space-y-4 mb-6">
            {currentId ? (
              <div className="clay-card bg-green p-4">
                <p className="text-sm font-medium text-text mb-2">Your current Kindness ID:</p>
                <p className="font-mono text-lg font-bold text-heading break-words">{currentId}</p>
              </div>
            ) : (
              <p className="text-text font-medium">
                Welcome to Nakamoto Verse! Choose how you'd like to proceed:
              </p>
            )}

            <div className="space-y-3">
              {!currentId && (
                <button
                  onClick={() => setMode('generate')}
                  className={`w-full p-4 rounded-xl text-left transition-all duration-300 focus-visible ${
                    mode === 'generate'
                      ? 'clay-button-purple shadow-lg transform scale-105'
                      : 'clay-card hover:transform hover:scale-105'
                  }`}
                >
                  <div className="flex items-center space-x-3">
                    <SafeIcon icon={FiRefreshCw} className="text-lg text-heading" />
                    <div>
                      <p className="font-bold text-heading">Generate New ID</p>
                      <p className="text-sm text-text">Get a fresh kindness-themed username</p>
                    </div>
                  </div>
                </button>
              )}

              <button
                onClick={() => setMode('enter')}
                className={`w-full p-4 rounded-xl text-left transition-all duration-300 focus-visible ${
                  mode === 'enter'
                    ? 'clay-button-green shadow-lg transform scale-105'
                    : 'clay-card hover:transform hover:scale-105'
                }`}
              >
                <div className="flex items-center space-x-3">
                  <SafeIcon icon={FiUser} className="text-lg text-heading" />
                  <div>
                    <p className="font-bold text-heading">Enter Existing ID</p>
                    <p className="text-sm text-text">Return with your saved Kindness ID</p>
                  </div>
                </div>
              </button>
            </div>
          </div>

          <form onSubmit={handleSubmit} className="space-y-4">
            {mode === 'generate' && (
              <div>
                <div className="clay-card bg-yellow p-4 mb-3">
                  <div className="flex flex-wrap items-center justify-between gap-3">
                    <span className="font-mono text-lg font-bold text-heading break-words flex-1">
                      {generatedId}
                    </span>
                    <button
                      type="button"
                      onClick={handleGenerateNew}
                      className="clay-button-pink p-2 hover:transform hover:scale-110 transition-all duration-300 focus-visible"
                      aria-label="Generate new ID"
                    >
                      <SafeIcon icon={FiRefreshCw} className="text-lg text-text" />
                    </button>
                  </div>
                </div>
                <p className="text-sm font-medium text-text">
                  ⚠️ Save this ID! There's no way to recover it if lost.
                </p>
              </div>
            )}

            {mode === 'enter' && (
              <div>
                <input
                  type="text"
                  value={inputId}
                  onChange={(e) => setInputId(e.target.value)}
                  placeholder="Enter your Kindness ID (e.g., gentle-panda-4832)"
                  className="clay-input w-full form-title-input text-text placeholder-gray-500 font-medium focus-visible"
                  required
                />
                <p className="text-sm font-medium text-text mt-2">
                  Enter the exact ID you saved from your previous visit.
                </p>
              </div>
            )}

            <button
              type="submit"
              className="clay-button w-full py-4 font-bold text-text text-lg hover:transform hover:scale-105 transition-all duration-300 focus-visible"
            >
              {mode === 'generate' ? 'Use This ID' : mode === 'enter' ? 'Continue' : 'Close'}
            </button>
          </form>

          {!currentId && (
            <div className="mt-4 clay-card bg-pink p-3">
              <p className="text-sm text-text text-center font-medium">
                Your Kindness ID is completely anonymous and stored only on your device. No personal information is collected.
              </p>
            </div>
          )}
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

export default UserIdModal;